/**
 * Preisfrische-Badge für Katalog-Treffer (Großhandels-Katalogartikel, DATANORM-Import).
 *
 * Zweck: der Nutzer sieht im Katalog-Vorschlags-Dialog JE TREFFER, wie aktuell der
 * angezeigte Preis ist, BEVOR er ihn übernimmt — heute gibt es dafür kein Signal,
 * obwohl die Daten (price_valid_from/until + updated_at) längst mitgeliefert werden
 * könnten. Reine Funktion ohne DB-/Store-Zugriff, damit sie unter node:test mit
 * festen Zeitstempeln geprüft werden kann.
 *
 * Schwellen (begründet):
 * - 'abgelaufen': `priceValidUntil` liegt VOR `now`. Ein Datum-nur-Wert (Mitternacht,
 *   häufigster Fall bei DATANORM-Gültigkeitsfenstern) zählt bis EINSCHLIESSLICH Ende
 *   seines Kalendertags — sonst würde ein "gültig bis heute"-Preis schon ab 00:00 Uhr
 *   als abgelaufen angezeigt. Bewusste Vereinfachung gegenüber der serverseitigen
 *   Berlin-Tagesgrenzen-Logik (server/routes/softec-db.ts::gueltigBisEinschliesslich):
 *   hier wird das UTC-Tagesende angesetzt (max. 1-2h Abweichung an der Tagesgrenze).
 *   Das ist für ein ANZEIGE-Badge vertretbar — der Server-Filter bleibt die
 *   maßgebliche Instanz und liefert abgelaufene Preise ohnehin gar nicht erst aus;
 *   dieses Badge fängt nur Datenstände ab, die auf anderem Weg in die Anzeige gelangen.
 * - 'frisch': Preisstand jünger als 30 Tage. Begründung: DATANORM-Preisdienste der
 *   Großhändler liefern typischerweise MONATLICHE Updates — ein Preisstand innerhalb
 *   eines Update-Intervalls ist so aktuell, wie er sein kann.
 * - 'aelter': alles dazwischen (Preisstand ≥ 30 Tage alt, aber Gültigkeit nicht
 *   überschritten) — wahrscheinlich wurde mindestens ein Katalog-Update verpasst,
 *   der Preis kann aber weiterhin formal gültig sein.
 * - 'unbekannt': keinerlei Datumsangabe vorhanden — es wird NICHT geraten
 *   (kein Badge statt eines falschen Badges).
 *
 * "Preisstand" = der JÜNGSTE der beiden Zeitstempel `priceValidFrom` (ab wann der
 * Preis gilt) und `updatedAt` (letzter Import/Update der Zeile). Beide sagen "so alt
 * sind diese Preisdaten mindestens" — der jüngere ist die ehrlichste Untergrenze.
 */

export type PreisFrischeStufe = 'frisch' | 'aelter' | 'abgelaufen' | 'unbekannt';

/** Schwelle in Tagen: Preisstand jünger ⇒ 'frisch' (DATANORM-Updates sind typisch monatlich). */
export const PREIS_FRISCH_MAX_ALTER_TAGE = 30;

export interface PreisFrischeQuelle {
  priceValidFrom?: string | Date | null;
  priceValidUntil?: string | Date | null;
  updatedAt?: string | Date | null;
}

export interface PreisFrischeErgebnis {
  stufe: PreisFrischeStufe;
  /** Alter des Preisstands in GANZEN Tagen (abgerundet), null wenn kein Stand bekannt. */
  alterTage: number | null;
  /** Fertiger, kurzer Anzeigetext fürs Badge (de-DE). */
  label: string;
}

const TAG_MS = 24 * 60 * 60 * 1000;

function parseZeitpunkt(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Datum-nur-Wert (UTC-Mitternacht) — dieselbe Erkennung wie serverseitig (UTC-Getter). */
function istDatumNurMitternacht(d: Date): boolean {
  return (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

/** Inklusive Obergrenze: Datum-nur gilt bis Ende seines (UTC-)Kalendertags, sonst exakt. */
function inklusiveObergrenzeMs(d: Date): number {
  if (!istDatumNurMitternacht(d)) return d.getTime();
  return d.getTime() + TAG_MS - 1;
}

/**
 * Bewertet die Preisfrische eines Katalog-Treffers. Reine Funktion — `now` wird
 * explizit übergeben, damit Tests deterministisch sind.
 */
export function bewertePreisFrische(quelle: PreisFrischeQuelle, now: Date): PreisFrischeErgebnis {
  const validFrom = parseZeitpunkt(quelle.priceValidFrom);
  const validUntil = parseZeitpunkt(quelle.priceValidUntil);
  const updatedAt = parseZeitpunkt(quelle.updatedAt);

  // 1. Hartes Kriterium zuerst: Gültigkeit überschritten ⇒ abgelaufen, unabhängig vom Alter.
  if (validUntil !== null && inklusiveObergrenzeMs(validUntil) < now.getTime()) {
    return { stufe: 'abgelaufen', alterTage: alterTageAus(validFrom, updatedAt, now), label: 'Preis abgelaufen' };
  }

  const alterTage = alterTageAus(validFrom, updatedAt, now);
  if (alterTage === null) {
    return { stufe: 'unbekannt', alterTage: null, label: 'Preisstand unbekannt' };
  }

  if (alterTage < PREIS_FRISCH_MAX_ALTER_TAGE) {
    return { stufe: 'frisch', alterTage, label: `Preisstand ${formatAlter(alterTage)}` };
  }
  return { stufe: 'aelter', alterTage, label: `Preisstand ${formatAlter(alterTage)} — evtl. veraltet` };
}

/** Jüngster bekannter Preisstand (validFrom vs. updatedAt) als Alter in ganzen Tagen. */
function alterTageAus(validFrom: Date | null, updatedAt: Date | null, now: Date): number | null {
  const kandidaten = [validFrom, updatedAt].filter((d): d is Date => d !== null);
  if (kandidaten.length === 0) return null;
  const juengster = Math.max(...kandidaten.map((d) => d.getTime()));
  const diff = now.getTime() - juengster;
  // Zukunfts-Stand (Uhrendrift / geplanter Preis, der der Server-Filter durchließ): Alter 0.
  return diff <= 0 ? 0 : Math.floor(diff / TAG_MS);
}

function formatAlter(alterTage: number): string {
  if (alterTage === 0) return 'heute';
  if (alterTage === 1) return 'gestern';
  return `vor ${alterTage} Tagen`;
}
